import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  BrowserRouter as Router,
  useHistory,
  useLocation,
} from "react-router-dom";
import { RootState, AppDispatch } from "store";
import { logout } from "store/slices/authSlice";
import { useUserLogoutMutation } from "features/auth/api/authApi";
import { updateSettings } from "store/slices/tradingSlice";
import { useInitAuth } from "hooks/useInitAuth";
import { useAnimatedNumber } from "hooks/useAnimatedNumber";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { OAUTH_CONFIG } from "config";

import "./App.css";

import Logo from "components/Logo";
import Modal from "components/Modal";
import { FinanceModal } from "widgets/finance-modal";

import WalletIcon from "./../assets/svg/wallet.svg";
import menuOpenIcon from "./../assets/svg/menu.svg";
import menuCloseIcon from "./../assets/svg/close.svg";
import UserIcon from "./../assets/svg/user.svg";

import RateLimitSnackbar from "components/RateLimitSnackbar";
import AppRouter from "app/router/AppRouter";
import { ProfileButton, UserProfile } from "features/user-profile";

const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isInitializing } = useInitAuth();
  const [userLogout] = useUserLogoutMutation();
  const { isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth,
  );
  const { balance, settings } = useSelector(
    (state: RootState) => state.trading,
  );
  const history = useHistory();
  const location = useLocation();
  const hideAppHeader =
    location.pathname === "/" || location.pathname === "/landing";

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Состояние для выпадающего списка выбора счета
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const accountSelectorRef = useRef<HTMLDivElement>(null);

  // Состояние для модального окна пополнения баланса
  const [isFinanceModalOpen, setIsFinanceModalOpen] = useState(false);

  // Обновление позиции выпадающего списка
  useEffect(() => {
    if (isAccountDropdownOpen && accountSelectorRef.current) {
      const rect = accountSelectorRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isAccountDropdownOpen]);

  // Обработчик клика вне выпадающего списка
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        accountSelectorRef.current &&
        !accountSelectorRef.current.contains(event.target as Node) &&
        !document
          .getElementById("account-dropdown")
          ?.contains(event.target as Node)
      ) {
        setIsAccountDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Форматирование баланса
  const formatBalance = (amount: number): string => {
    const isNegative = amount < 0;
    const fixed = Math.abs(amount).toFixed(2);
    const [integerPart, fractionPart] = fixed.split(".");

    let groupedInteger = "";
    for (let i = 0; i < integerPart.length; i += 1) {
      const posFromEnd = integerPart.length - i;
      if (i > 0 && posFromEnd % 3 === 0) {
        groupedInteger += " ";
      }
      groupedInteger += integerPart[i];
    }

    return `${isNegative ? "-" : ""}${groupedInteger}.${fractionPart}`;
  };

  // Обработчик смены типа счета
  const handleAccountChange = (accountType: string) => {
    if (accountType === "demo") {
      dispatch(
        updateSettings({
          demo_balance: true,
          real_balance: false,
          accountPreferenceSaved: true,
        }),
      );
    } else if (accountType === "real") {
      dispatch(
        updateSettings({
          demo_balance: false,
          real_balance: true,
          accountPreferenceSaved: true,
        }),
      );
    }
    setIsAccountDropdownOpen(false);
  };

  // Определяем текущий выбранный тип счета
  const getCurrentAccountType = () => {
    if (settings.demo_balance) return "Demo";
    if (settings.real_balance) return "Real";
    return "Not Selected";
  };

  const rawBalance = settings.demo_balance
    ? balance.demo
    : settings.real_balance
      ? balance.real
      : 0;
  const animatedBalance = useAnimatedNumber(rawBalance);

  // Эффект для установки демо-счета по умолчанию при первой загрузке
  useEffect(() => {
    // Не трогаем, если тип счёта уже определён (вручную или по балансу из type 17)
    if (
      isAuthenticated &&
      !settings.demo_balance &&
      !settings.real_balance &&
      !settings.accountPreferenceSaved
    ) {
      dispatch(
        updateSettings({
          demo_balance: true,
          real_balance: false,
        }),
      );
    }
  }, [
    dispatch,
    settings.demo_balance,
    settings.real_balance,
    settings.accountPreferenceSaved,
    isAuthenticated,
  ]);

  // Обработчик выхода: очищаем состояние и переходим на страницу логина

  const handleLogout = async () => {
    try {
      await userLogout().unwrap();
    } catch {
      // сервер снял куку или недоступен — всё равно разлогиниваем локально
    } finally {
      dispatch(logout());
    }
  };

  if (isInitializing) {
    return null;
  }

  return (
    <div className="app">
      {!hideAppHeader && (
        <header className="app-header">
          <div className="header-left">
            <Logo />
          </div>

          <div className="header-right">
            {isAuthenticated && user ? (
              <>
                <div
                  className={`menu-layer ${isMenuOpen ? "open" : ""}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div
                    className={`menu`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <div
                      className="button account-selector"
                      ref={accountSelectorRef}
                      onClick={() =>
                        setIsAccountDropdownOpen(!isAccountDropdownOpen)
                      }
                    >
                      <img
                        className="account-selector__logo"
                        src={
                          settings.real_balance
                            ? "/assets/svg/crown-green.svg"
                            : "/assets/svg/gamepad.svg"
                        }
                        alt="Accout type logo"
                      />
                      <span className="account-selector__label">Account:</span>
                      <span
                        className={`account-selector__type ${settings.real_balance ? "real" : "demo"}`}
                      >
                        {getCurrentAccountType()}
                      </span>
                    </div>

                    <div className="menu__item">
                      <div className="button balance">
                        <span className="balance__label">Balance:</span>
                        <div className="balance__amount">
                          <span className="balance__value">
                            {formatBalance(animatedBalance)}
                          </span>
                          <span className="balance__currency">$</span>
                        </div>
                      </div>

                      <button
                        className="button deposit-button"
                        onClick={() => setIsFinanceModalOpen(true)}
                        title="Deposit Funds"
                      >
                        <img src="/assets/svg/plus.svg" alt="Deposit icon" />
                        Deposit
                      </button>

                      <Modal
                        isOpen={isFinanceModalOpen}
                        onClose={() => setIsFinanceModalOpen(false)}
                        icon={WalletIcon}
                        title="Deposit Funds"
                      >
                        <FinanceModal
                          onClose={() => setIsFinanceModalOpen(false)}
                        />
                      </Modal>
                    </div>

                    <ProfileButton
                      className="button user-profile-button"
                      onClick={() => setIsProfileModalOpen(true)}
                    />

                    <Modal
                      isOpen={isProfileModalOpen}
                      onClose={() => setIsProfileModalOpen(false)}
                      icon={UserIcon}
                      title="User Profile"
                      className="user-profile-modal"
                    >
                      <UserProfile />
                    </Modal>

                    <button
                      className="button login-button"
                      onClick={handleLogout}
                    >
                      <span className="text">Logout</span>
                      <img src="/assets/svg/logout.svg" alt="Logout icon" />
                    </button>
                  </div>
                </div>

                <button
                  className="button menu-button"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                  Menu
                  <img
                    src={isMenuOpen ? menuCloseIcon : menuOpenIcon}
                    alt="Menu icon"
                  />
                </button>
              </>
            ) : (
              <button
                className="button login-button"
                onClick={() => history.push("/login")}
              >
                Login
                <img src="/assets/svg/login.svg" alt="Login icon" />
              </button>
            )}
          </div>
        </header>
      )}

      <main className="app-main">
        <AppRouter />
      </main>

      {/* Выпадающий список счетов в конце DOM-дерева */}
      {isAccountDropdownOpen && isAuthenticated && (
        <div
          id="account-dropdown"
          className="account-dropdown-portal"
          style={{
            position: "absolute",
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          <div
            className={`account-option ${settings.demo_balance && !settings.real_balance ? "active" : ""}`}
            onClick={() => handleAccountChange("demo")}
          >
            Demo Account
          </div>
          <div
            className={`account-option ${!settings.demo_balance && settings.real_balance ? "active" : ""}`}
            onClick={() => handleAccountChange("real")}
          >
            Real Account
          </div>
        </div>
      )}

      {/* Global rate-limit snackbar */}
      <RateLimitSnackbar />
    </div>
  );
};

const App: React.FC = () => {
  // const initialized = useRef(false);

  return (
    <Router>
      <GoogleOAuthProvider clientId={OAUTH_CONFIG.google.clientId}>
        <AppContent />
      </GoogleOAuthProvider>
    </Router>
  );
};

export default App;
